import { Component } from '@angular/core';
import { Profile } from 'src/app/data/schema/profile';
import { ModalController } from '@ionic/angular';
import { AuthenticationService } from 'src/app/core/modules/auth-core/data/service/authentication.service';
import { BasePage } from 'src/app/core/base-page';
import { Raffle, RaffleStatus } from 'src/app/data/schema/raffle';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { FieldToValidate, FormHelper } from 'src/app/core/helper/form-helper';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.page.html',
  styleUrls: ['./profile.page.scss'],
})
export class ProfilePage extends BasePage {

  profile: Profile = new Profile(this.authService.profile);
  showUserChange = false;
  newUsername: string;

  private origProfile: string;


  constructor(
    authService: AuthenticationService,
    private modalCtrl: ModalController
  ) {
    super(authService);
  }

  ionViewWillEnter() {
    // Save state
    this.saveProfileState();
    this.newUsername = this.profile.username;
  }

  async close() {
    await this.modalCtrl.dismiss();
  }

  async save() {
    if (this.wasProfileModified() === false || !this.validate()) {
      return;
    }

    const isNew = (this.profile.username !== this.newUsername);
    if (isNew) {
      this.profile.pendingUsername = this.newUsername;
    }

    await this.profile.save();
    this.authService.updateProfile(this.profile);

    if (isNew) {
      this.showUserChange = true;
    } else {
      this.close();
    }
  }

  async promptForDelete() {

    const confirm = await ServiceHelper.confirmSomething(
      'Remove Account?',
      'Are you sure you wish to delete your account? This is not reversible.'
    );

    if (confirm === true) {
      this.delete();
    }
  }

  countryChanged() {
    this.profile.province = undefined; // reset the province so it's forced to be selected
  }

  private validate() {
    // basic required fields
    const fields: FieldToValidate[] = [
      {
        name: 'First Name',
        value: this.profile.firstName
      },
      {
        name: 'Last Name',
        value: this.profile.lastName
      },
      {
        name: 'Email Address',
        value: this.profile.username
      },
      {
        name: 'Country',
        value: this.profile.country
      }
    ];
    // if the user is in canada/us we need to have a province/state
    if (this.profile.country === 'CA' || this.profile.country === 'US') {
      fields.push({
        name: (this.profile.country === 'CA') ? 'Province' : 'State',
        value: this.profile.province
      });
    }
    // test for missing fields, if we don't have any we need to test for form level
    // errors, if we have any let the user know
    const allFilledIn = FormHelper.checkForEmptyField(fields);
    if (allFilledIn === true) {
      const isFormValid = document.getElementsByClassName('invalid-el').length === 0;
      if (isFormValid === false) {
        ServiceHelper.displayAlert('Form Errors', 'Please correct the form errors before saving.');
      }
      return isFormValid;
    } else {
      return false;
    }
  }

  private async delete() {
    try {
      this.busy = true;

      // first get all raffles to check if there are any active
      const raffles = await Raffle.fetchAll();
      const hasLive = raffles.filter(r => r.status === RaffleStatus.Active).length > 0;
      if (hasLive) {
        ServiceHelper.displayAlert('Live Raffle Detected', 'You cannot delete your account while you have a Raffle running.');
        return;
      }

      await this.profile.delete(this.pageState);
      ServiceHelper.displayAlert('Success', 'Account deleted successfully!');
      this.authService.logout();
      await this.modalCtrl.dismiss();
      ServiceHelper.navCtrl.navigateRoot('/auth/login');
    } catch (error) {
      console.error('Error deleting account. Error', error);
      ServiceHelper.displayAlert('Error deleting account', error);
    } finally {
      this.busy = false;
    }
  }

  private wasProfileModified() {
    if (this.newUsername !== this.profile.username) {
      return true;
    }
    return (JSON.stringify(this.profile) !== this.origProfile);
  }

  private saveProfileState() {
    this.origProfile = JSON.stringify(this.profile);
  }

}
